import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaChartLine, FaUserTie, FaShieldAlt, FaRegLightbulb } from 'react-icons/fa';

const FinancialAdvisingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', path: '/services' },
    { label: 'Financial Advising' }
  ];

  const advisoryServices = [
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "Investment Planning",
      description: "Customized investment strategies aligned with your financial goals, risk tolerance, and time horizon."
    },
    {
      icon: <FaUserTie className="w-8 h-8 text-secondary" />,
      title: "Retirement Planning",
      description: "Comprehensive retirement strategies to ensure financial security and maintain your desired lifestyle after retirement."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "Risk Management",
      description: "Strategies to protect your assets and mitigate risks through appropriate insurance and diversification."
    },
    {
      icon: <FaRegLightbulb className="w-8 h-8 text-secondary" />,
      title: "Wealth Preservation",
      description: "Advanced strategies for preserving wealth and establishing a lasting financial legacy for future generations."
    }
  ];

  const advisoryProcess = [
    {
      title: "Discovery Meeting",
      description: "We begin with a comprehensive assessment of your current financial situation, goals, and unique challenges."
    },
    {
      title: "Strategy Development",
      description: "Our team develops a tailored financial strategy addressing your specific needs and long-term objectives."
    },
    {
      title: "Implementation",
      description: "We execute your personalized financial plan with precision and attention to detail."
    },
    {
      title: "Ongoing Management",
      description: "Regular reviews and adjustments ensure your financial strategy evolves with your changing life circumstances."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Financial Advising" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Expert Financial Guidance
              </h2>
              <p className="text-lg text-gray-700">
                Segway Capital's Financial Advising service provides personalized financial guidance to help you navigate complex financial decisions and achieve your long-term goals.
              </p>
              <p className="text-lg text-gray-700">
                Our team of experienced financial advisors combines industry expertise with a deep understanding of your unique circumstances to create tailored strategies that align with your objectives.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Schedule a Consultation
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/people-discussing-business-plan.jpg" 
                alt="Financial Advising Session" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our Advisory Services
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We offer a comprehensive suite of financial advisory services designed to address all aspects of your financial life.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {advisoryServices.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{service.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{service.title}</h4>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img 
                src="/assets/business-team-working-together.jpg" 
                alt="Financial Advisory Team" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6 order-1 lg:order-2"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary">
                Our Advisory Process
              </h3>
              <p className="text-lg text-gray-700">
                We follow a structured yet flexible process to ensure your financial strategy is comprehensive and personalized:
              </p>
              
              <div className="space-y-4">
                {advisoryProcess.map((step, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-secondary">
                    <h4 className="text-xl font-heading font-semibold mb-2 text-primary">{step.title}</h4>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
          
          <div className="mt-16 bg-primary text-white rounded-xl p-10 shadow-lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold mb-4">Ready to Secure Your Financial Future?</h3>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Our team of experienced financial advisors is ready to help you navigate your financial journey with confidence.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Link 
                to="/contact-us" 
                className="px-8 py-3 bg-white text-primary hover:bg-gray-100 font-semibold rounded-lg transition-colors inline-block"
              >
                Schedule Your Financial Consultation
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FinancialAdvisingPage;
